import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

/**
 * useLocalStorage
 *
 * Persist the state with local storage so that it remains after a page refresh.
 */

export type SetValue<T> = Dispatch<SetStateAction<T>>;

const isJsonString = (item: string) => {
  try {
    JSON.parse(item);
  } catch {
    return false;
  }
  return true;
};

export const parseJSON = <T>(value: string | null): T | undefined => {
  try {
    return value === "undefined" || value === undefined
      ? undefined
      : JSON.parse(value ?? "");
  } catch {
    return undefined;
  }
};

export const useLocalStorage = <T>(
  key: string,
  initialValue: T
): [T, SetValue<T>] => {
  const readValue = useCallback((): T => {
    try {
      const item = localStorage.getItem(key);

      if (!item) return initialValue;

      if (!isJsonString(item) && typeof initialValue === "string") {
        const stringifiedItem = JSON.stringify(item);
        localStorage.setItem(key, stringifiedItem);
        return parseJSON(stringifiedItem) as T;
      }

      return parseJSON(item) as T;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initialValue;
    }
  }, [initialValue, key]);

  const [storedValue, setStoredValue] = useState<T>(readValue);

  const setValue = useCallback<SetValue<T>>(
    (value) => {
      try {
        const newValue = value instanceof Function ? value(storedValue) : value;

        localStorage.setItem(key, JSON.stringify(newValue));

        setStoredValue(newValue);
      } catch (error) {
        console.warn(`Error setting localStorage key “${key}”:`, error);
      }
    },
    [key, storedValue]
  );

  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [storedValue, setValue];
};
